import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const ShortURLGenerator = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [shortURLs, setShortURLs] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUrl, setEditingUrl] = useState(null);
  const [editServiceId, setEditServiceId] = useState('');
  const [editCoupons, setEditCoupons] = useState([]);
  const [editCouponId, setEditCouponId] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || user?.uuid || JSON.parse(localStorage.getItem('user') || '{}')?.uuid;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchServices();
    fetchShortURLs();
  }, [userRole, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${apiUrl}/services`, { headers });
      setServices(response.data || []);
    } catch (err) {
      setError('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const fetchShortURLs = async () => {
    try {
      const response = await axios.get(`${apiUrl}/practitioners/${userId}/short-urls`, { headers });
      setShortURLs(response.data || []);
    } catch (err) {
      setError('Failed to fetch short URLs');
    }
  };

  useEffect(() => {
    if (!selectedService) {
      setAvailableCoupons([]);
      setSelectedCoupon('');
      return;
    }
    axios.get(`${apiUrl}/services/${selectedService}/coupons`, { headers })
      .then((res) => setAvailableCoupons(res.data || []))
      .catch(() => setAvailableCoupons([]));
    setSelectedCoupon('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService]);

  const generateURL = async () => {
    if (!selectedService) return;
    setError('');
    try {
      await axios.post(
        `${apiUrl}/practitioners/${userId}/short-urls`,
        { service_id: selectedService, coupon_id: selectedCoupon || null },
        { headers }
      );
      setSelectedService('');
      setSelectedCoupon('');
      fetchShortURLs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate URL');
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    alert('URL copied!');
  };

  const deactivateURL = async (urlId) => {
    setError('');
    try {
      await axios.delete(`${apiUrl}/practitioners/${userId}/short-urls/${urlId}`, { headers });
      fetchShortURLs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate URL');
    }
  };

  const openEdit = (urlRecord) => {
    setEditingUrl(urlRecord);
    setEditServiceId(urlRecord.service_id);
    setEditCouponId(urlRecord.coupon_id || '');
    axios.get(`${apiUrl}/services/${urlRecord.service_id}/coupons`, { headers })
      .then((res) => setEditCoupons(res.data || []))
      .catch(() => setEditCoupons([]));
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await axios.patch(
        `${apiUrl}/practitioners/${userId}/short-urls/${editingUrl.id}`,
        { service_id: editServiceId, coupon_id: editCouponId || null },
        { headers }
      );
      setEditingUrl(null);
      fetchShortURLs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update short URL');
    }
  };

  if (loading) return <div className="page-container">Loading...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Short URL Generator</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/dashboard')}>
          Back to Dashboard
        </button>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="card">
        <div className="card-header">Generate New Short URL</div>
        <div className="form-group">
          <label>Select Service</label>
          <select value={selectedService} onChange={(e) => setSelectedService(e.target.value)}>
            <option value="">-- Choose a service --</option>
            {services.map(service => (
              <option key={service.id} value={service.id}>
                {service.name} (${service.price})
              </option>
            ))}
          </select>
        </div>
        {selectedService && (
          <div className="form-group">
            <label>Attach a Coupon (optional)</label>
            <select value={selectedCoupon} onChange={(e) => setSelectedCoupon(e.target.value)}>
              <option value="">No coupon</option>
              {availableCoupons.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.discount_type === 'percent' ? `${parseFloat(c.discount_value)}% off` : `$${parseFloat(c.discount_value).toFixed(2)} off`}
                </option>
              ))}
            </select>
            {availableCoupons.length === 0 && (
              <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 4 }}>No active coupons apply to this service yet - ask an admin to create one.</p>
            )}
          </div>
        )}
        <button className="btn btn-primary" onClick={generateURL} disabled={!selectedService}>
          Generate URL
        </button>
      </div>

      <div className="list-container table-wrap">
        <div className="card-header">Active Short URLs</div>
        <table className="table">
          <thead>
            <tr><th>Service</th><th>Link</th><th>Coupon</th><th>Clicks</th><th></th></tr>
          </thead>
          <tbody>
            {shortURLs.map(urlRecord => (
              <tr key={urlRecord.id}>
                <td>{urlRecord.service_name}</td>
                <td className="text-muted">{urlRecord.short_code}</td>
                <td>{urlRecord.coupon_code ? <span className="badge badge-info">{urlRecord.coupon_code}</span> : <span className="text-muted">—</span>}</td>
                <td>{urlRecord.click_count || 0}</td>
                <td>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ marginRight: 6 }}
                    onClick={() => copyToClipboard(`https://hadmediumship.com/register-via-link?code=${urlRecord.short_code}`)}
                  >
                    Copy Link
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => openEdit(urlRecord)}>
                    Edit
                  </button>
                  <button className="btn btn-danger btn-sm" onClick={() => deactivateURL(urlRecord.id)}>
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
            {shortURLs.length === 0 && <tr><td colSpan="5" className="empty-state">No short URLs generated yet</td></tr>}
          </tbody>
        </table>
      </div>

      {editingUrl && (
        <div className="modal" onClick={() => setEditingUrl(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Short Link</h2>
              <button className="close-btn" onClick={() => setEditingUrl(null)}>×</button>
            </div>
            <form onSubmit={saveEdit}>
              <div className="form-group">
                <label>Points to service</label>
                <select
                  value={editServiceId}
                  onChange={(e) => {
                    const newServiceId = e.target.value;
                    setEditServiceId(newServiceId);
                    setEditCouponId('');
                    axios.get(`${apiUrl}/services/${newServiceId}/coupons`, { headers })
                      .then((res) => setEditCoupons(res.data || []))
                      .catch(() => setEditCoupons([]));
                  }}
                  required
                >
                  {services.map(service => (
                    <option key={service.id} value={service.id}>
                      {service.name} (${service.price})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Attach a Coupon (optional)</label>
                <select value={editCouponId} onChange={(e) => setEditCouponId(e.target.value)}>
                  <option value="">No coupon</option>
                  {editCoupons.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {c.discount_type === 'percent' ? `${parseFloat(c.discount_value)}% off` : `$${parseFloat(c.discount_value).toFixed(2)} off`}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn-primary btn-block">Save Changes</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShortURLGenerator;
